/**
 * AtlasRacingModule — the GRAND CIRCUIT RACING SEASON on the client (TOWN LIFE, 2026-09-24).
 *
 * The server owns the schedule (a race every 30 min), the grid law, the gates and the season board
 * (server/src/lib/race_ops.ts). This module:
 *   QUEUE    the Town window's Races tab: the next race, Join / Leave, the grid window, a waypoint to the line.
 *   GRID     a HUD countdown while you are carded: where the start line is and whether you are on it (riding,
 *            within 220 m) — a racer not on the grid when the lights go out MISSES the race.
 *   RACE     your position in the field, the next gate and the race clock; when you ride within reach of the gate
 *            you must cross next it is reported (`raceGate`) — the server credits it only if the row agrees.
 *   RIVALS   the named NPC rivals drawn on the track: each one's progress is ARITHMETIC from the race's seed
 *            (shared/race_season.ts npcArcAt — the server finishes them on the same numbers), placed on the
 *            circuit polyline with a hovercraft body, a livery-coloured name plate and a hover bob.
 *   RESULTS  the finish: places, points, a light show over the line; the season's leaderboard in the Races tab.
 * Levers: `__racing`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import type { Identity } from 'spacetimedb';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { peekCircuit, type Circuit } from '../lib/race_circuit.js';
import {
  circuitFor, gatePos, expectedGate, gatesToFinish, npcArcAt, npcFinishMs, seasonOf, seasonStartS, NPC_ROSTER, GRID_R_M, GATE_R_M, RACE_LAPS,
} from '../../../shared/race_season.js';
import { untilText, compassOf, raceTime } from '../lib/town_life.js';
import { commonsSeedNow } from '../lib/town_names.js';
import { el, btn, head, chip, muted, card, act, debounced, ms } from './town/town_ui.js';
import { registerTownTab } from './town/town_tabs.js';
import { softRender } from '../lib/soft_render.js';
import { deferDisposeTree } from '../lib/defer_dispose.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import type { AtlasVehicleModule } from './AtlasVehicleModule.js';

interface EventRow { raceId: bigint; season: number; state: string; startsAt: { microsSinceUnixEpoch: bigint }; gridAt: { microsSinceUnixEpoch: bigint }; laps: number; players: number; field: number; winnerName: string; finishedAt: { microsSinceUnixEpoch: bigint } }
interface EntryRow { entryId: bigint; raceId: bigint; identity: Identity; npc: boolean; rival: number; name: string; state: string; gatesPassed: number; lastGateAt: { microsSinceUnixEpoch: bigint }; finishMs: number; place: number; points: number }
interface StandingRow { standingKey: string; season: number; racerKey: string; identity: Identity; npc: boolean; name: string; points: number; wins: number; podiums: number; starts: number; bestMs: number }

/** Report a gate a little inside the server's radius so a row that trails the client still lands inside it. */
const GATE_REPORT_M = GATE_R_M - 18;
const RIVAL_DRAW_M = 1800;
const RIVAL_HOVER_M = 1.0;

interface Rival { entryId: string; rival: number; body: THREE.Object3D; plate: THREE.Sprite; fallback: boolean }

function plateFor(name: string, livery: string): THREE.Sprite {
  const c = document.createElement('canvas');
  c.width = 512; c.height = 96;
  const g = c.getContext('2d')!;
  g.fillStyle = 'rgba(10,10,12,0.62)';
  g.fillRect(0, 14, 512, 68);
  g.fillStyle = livery;
  g.fillRect(0, 14, 14, 68);
  g.font = '700 42px Sora, sans-serif';
  g.textAlign = 'center'; g.textBaseline = 'middle';
  g.fillStyle = '#f4efe2';
  g.fillText(name, 262, 49);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  const m = new THREE.SpriteMaterial({ map: t, transparent: true, depthWrite: false });
  markMrtOverlay(m);
  const s = new THREE.Sprite(m);
  s.scale.set(4.4, 0.82, 1);
  s.position.y = 3.2;
  return s;
}

export class AtlasRacingModule implements IModule {
  readonly name = 'AtlasRacing';
  private app!: App;
  private scene!: THREE.Scene;
  private subbed = false;
  private hud: HTMLDivElement | null = null;
  private rivals = new Map<string, Rival>();
  private rivalsRace: bigint | null = null;
  private lastGateTry = 0;
  private gateNote = '';
  private finishSeen = new Set<string>();
  private holdMs: number | null = null;
  private tabEl: HTMLElement | null = null;
  private readonly rerenderTab = debounced(() => { if (this.tabEl?.isConnected) this.renderTab(this.tabEl); }, 250);

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    const h = document.createElement('div');
    h.id = 'race-hud';
    // Top-centre, under the wallet and the waypoint strip: the left column is the status panel's.
    h.style.cssText = 'position:fixed;top:132px;left:50%;transform:translateX(-50%);z-index:31;display:none;flex-direction:column;align-items:center;gap:4px;min-width:260px;max-width:420px;pointer-events:none;'
      + 'padding:8px 12px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 84%, transparent);border:1px solid color-mix(in srgb, var(--ax-accent) 45%, transparent);'
      + 'font:600 13px var(--ax-font);color:var(--ax-text)';
    document.body.appendChild(h);
    this.hud = h;
    app.world.addSystem(makeSystem('RaceSeason', Phase.UI, 31, () => this.tick()));
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    bus.on('atlas:world-cleared', () => this.clearRivals());
    registerTownTab({ key: 'races', label: 'Races', order: 1, render: (host) => { this.tabEl = host; this.renderTab(host); } });
    (window as unknown as Record<string, unknown>).__racing = { state: () => this.state(), join: () => this.join(), leave: () => this.leave(), hold: (t: number | null) => { this.holdMs = t; } };
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subbed) return;
    this.subbed = true;
    try { conn.subscriptionBuilder().subscribe(['SELECT * FROM race_event', 'SELECT * FROM race_entry', 'SELECT * FROM race_standing']); } catch { /* reconnect */ }
    const db = conn.db as unknown as Record<string, { onInsert?(cb: () => void): void; onUpdate?(cb: () => void): void; onDelete?(cb: () => void): void } | undefined>;
    for (const n of ['raceEvent', 'raceEntry', 'raceStanding']) { const t = db[n]; t?.onInsert?.(() => this.rerenderTab()); t?.onUpdate?.(() => this.rerenderTab()); t?.onDelete?.(() => this.rerenderTab()); }
  }

  // ── Rows ────────────────────────────────────────────────────────────────────
  private events(): EventRow[] { const t = (atlas.conn?.db as unknown as { raceEvent?: { iter(): Iterable<EventRow> } } | undefined)?.raceEvent; return t ? [...t.iter()] : []; }
  private entries(raceId?: bigint): EntryRow[] {
    const t = (atlas.conn?.db as unknown as { raceEntry?: { iter(): Iterable<EntryRow> } } | undefined)?.raceEntry;
    if (!t) return [];
    const all = [...t.iter()];
    return raceId === undefined ? all : all.filter((e) => e.raceId === raceId);
  }
  private standings(season: number): StandingRow[] {
    const t = (atlas.conn?.db as unknown as { raceStanding?: { iter(): Iterable<StandingRow> } } | undefined)?.raceStanding;
    return t ? [...t.iter()].filter((s) => s.season === season) : [];
  }
  private myLive(): { e: EntryRow; ev: EventRow } | null {
    const me = atlas.conn?.identity;
    if (!me) return null;
    for (const e of this.entries()) {
      if (e.npc || !e.identity.isEqual(me) || (e.state !== 'queued' && e.state !== 'racing' && e.state !== 'finished')) continue;
      const ev = this.events().find((x) => x.raceId === e.raceId);
      if (!ev) continue;
      if (ev.state === 'queue' || ev.state === 'grid' || ev.state === 'running') return { e, ev };
    }
    return null;
  }
  private circuit(): Circuit | null { const s = commonsSeedNow(); return s ? peekCircuit(s) : null; }
  private gates() { const s = commonsSeedNow(); return s ? circuitFor(s) : null; }

  private join(): Promise<boolean> { return act((atlas.conn?.reducers as unknown as { joinRaceQueue?(a: Record<string, never>): Promise<void> } | undefined)?.joinRaceQueue?.({}), 'You are on the race card — be on the grid, riding, when the lights go out', () => this.rerenderTab()); }
  private leave(): Promise<boolean> { return act((atlas.conn?.reducers as unknown as { leaveRaceQueue?(a: Record<string, never>): Promise<void> } | undefined)?.leaveRaceQueue?.({}), 'You left the race card', () => this.rerenderTab()); }

  // ── The frame ───────────────────────────────────────────────────────────────
  private tick(): void {
    const inCommons = atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId && !atlas.spaceFlight;
    const live = inCommons ? this.myLive() : null;
    this.hudTick(live);
    this.drawRivals(inCommons);
    this.noticeFinishes();
  }

  private hudTick(live: { e: EntryRow; ev: EventRow } | null): void {
    const h = this.hud;
    if (!h) return;
    if (!live) { if (h.style.display !== 'none') h.style.display = 'none'; return; }
    const g = this.gates();
    const now = Date.now();
    const lines: string[] = [];
    const { e, ev } = live;
    if (ev.state === 'queue' || ev.state === 'grid') {
      const toStart = ms(ev.startsAt) - now;
      lines.push(`GRAND CIRCUIT — lights in ${untilText(toStart)}`);
      if (g) {
        const dx = g.start[0] - atlas.localX, dz = g.start[1] - atlas.localZ;
        const d = Math.hypot(dx, dz);
        const riding = atlas.hoverRide;
        if (d <= GRID_R_M && riding) lines.push('On the grid — hold here.');
        else lines.push(`${d > 1000 ? `${(d / 1000).toFixed(1)} km` : `${Math.round(d)} m`} ${compassOf(dx, dz)} to the start line${riding ? '' : ' · mount a hovercraft'}`);
        if (ev.state === 'grid' && (d > GRID_R_M || !riding)) lines.push('Not on the grid — you will MISS the race.');
      }
    } else if (ev.state === 'running') {
      const tMs = now - ms(ev.startsAt);
      if (e.state === 'finished') lines.push(`FINISHED — ${raceTime(e.finishMs)}${e.place ? ` · P${e.place}` : ''}`);
      else {
        const pos = this.positionOf(ev, e);
        lines.push(`P${pos.place}/${pos.field} · ${raceTime(tMs)}`);
        if (g) {
          const next = expectedGate(g, e.gatesPassed);
          const [gx, gz] = gatePos(g, next);
          const d = Math.hypot(gx - atlas.localX, gz - atlas.localZ);
          lines.push(`Gate ${e.gatesPassed + 1}/${gatesToFinish(g, ev.laps || RACE_LAPS)}${next === 0 ? ' (the line)' : ''} — ${d > 1000 ? `${(d / 1000).toFixed(1)} km` : `${Math.round(d)} m`} ${compassOf(gx - atlas.localX, gz - atlas.localZ)}`);
          this.reportGate(ev, e, next, d);
        }
        if (this.gateNote) lines.push(this.gateNote);
      }
    }
    const sig = lines.join('\n');
    if (h.dataset.sig !== sig) { h.dataset.sig = sig; h.replaceChildren(...lines.map((l, i) => el('div', i === 0 ? 'color:var(--ax-hi)' : 'font-weight:500;color:var(--ax-soft)', l))); }
    if (h.style.display !== 'flex') h.style.display = 'flex';
  }

  /** Report the gate the server expects next once I ride inside it (throttled; the server judges the row). */
  private reportGate(ev: EventRow, e: EntryRow, gate: number, d: number): void {
    if (d > GATE_REPORT_M || !atlas.hoverRide) return;
    const now = performance.now();
    if (now - this.lastGateTry < 1200) return;
    this.lastGateTry = now;
    const r = atlas.conn?.reducers as unknown as { raceGate?(a: { raceId: bigint; gate: number }): Promise<void> } | undefined;
    r?.raceGate?.({ raceId: ev.raceId, gate })
      .then(() => { this.gateNote = gate === 0 ? '' : `Gate ${e.gatesPassed + 1} ✓`; })
      .catch((err: Error) => { this.gateNote = String(err?.message ?? err).replace(/^SenderError:\s*/, ''); });
  }

  /** My place in the field: rivals by their arithmetic arc, players by gates passed (+ time since the last gate). */
  private positionOf(ev: EventRow, me: EntryRow): { place: number; field: number } {
    const g = this.gates();
    if (!g) return { place: 1, field: 1 };
    const now = Date.now();
    const tMs = now - ms(ev.startsAt);
    const seed = `race:${ev.raceId}`;
    const n = g.sectors.length;
    const progress = (x: EntryRow): number => {
      if (x.state === 'finished') return 1e9 - x.finishMs;
      if (x.npc) {
        if (tMs >= x.finishMs) return 1e9 - x.finishMs;
        const a = npcArcAt(g, x.rival, seed, tMs, ev.laps || RACE_LAPS);
        return a.lap * g.length + a.s;
      }
      const lapIdx = Math.floor(x.gatesPassed / n);
      const k = x.gatesPassed % n;
      const s = g.sectors[k][0];
      return lapIdx * g.length + s + Math.min(g.sectors[k][1] - s, (now - ms(x.lastGateAt)) / 1000 * 38);
    };
    const field = this.entries(ev.raceId).filter((x) => x.state === 'racing' || x.state === 'finished');
    const mine = progress(me);
    let ahead = 0;
    for (const x of field) if (x.entryId !== me.entryId && progress(x) > mine) ahead++;
    return { place: ahead + 1, field: field.length };
  }

  // ── Rivals on the track ────────────────────────────────────────────────────
  private drawRivals(inCommons: boolean): void {
    const running = inCommons ? this.events().find((e) => e.state === 'running') : undefined;
    const g = this.gates();
    const c = this.circuit();
    if (!running || !g || !c || c.pts.length < 2) { this.clearRivals(); return; }
    if (this.rivalsRace !== running.raceId) { this.clearRivals(); this.rivalsRace = running.raceId; }
    const now = Date.now();
    // A probe may HOLD the drawing at a race time (a picture of the grid under a 2-fps software renderer); the race runs on.
    const tMs = this.holdMs ?? (now - ms(running.startsAt));
    const seed = `race:${running.raceId}`;
    const vehicles = this.app.getModule<AtlasVehicleModule>('AtlasVehicle');
    const keep = new Set<string>();
    const field = this.entries(running.raceId).filter((e) => e.npc).sort((x, y) => (x.entryId < y.entryId ? -1 : 1));
    for (const [slot, e] of field.entries()) {
      const a = npcArcAt(g, e.rival, seed, Math.min(tMs, npcFinishMs(g, e.rival, seed, running.laps || RACE_LAPS) + 4000), running.laps || RACE_LAPS);
      // THE GRID: each rival starts on its own grid slot — two abreast, 11 m apart, behind the line — and the formation
      // melts into the racing line over the first 400 m (a drawing only: the server's times are the rivals' truth).
      const fade = a.done ? 0 : Math.max(0, 1 - a.s / 400);
      const sDraw = (a.done ? 40 : a.s) - 11 * (1 + Math.floor(slot / 2)) * fade;
      const p0 = this.pointAt(c, sDraw);
      if (!p0) continue;
      const q0 = this.pointAt(c, sDraw + 3) ?? p0;
      const tl = Math.hypot(q0.x - p0.x, q0.z - p0.z) || 1;
      const lat = (slot % 2 === 0 ? -5 : 5) * fade;
      const p = { x: p0.x + (-(q0.z - p0.z) / tl) * lat, y: p0.y, z: p0.z + ((q0.x - p0.x) / tl) * lat };
      if (Math.hypot(p.x - atlas.localX, p.z - atlas.localZ) > RIVAL_DRAW_M) continue;
      const id = e.entryId.toString();
      keep.add(id);
      let r = this.rivals.get(id);
      const key = ['hover_falcon', 'hover_raptor', 'hover_tempest', 'hover_dune'][e.rival % 4];
      // A body built before the cooked hull landed is the procedural skimmer: swap it once the hull is in.
      if (!r || (r.fallback && vehicles?.hovercraftReady(key))) {
        // The skimmer being swapped for the cooked hull: its own geometry/materials go (the plate is reused below).
        if (r) { r.body.remove(r.plate); deferDisposeTree(r.body); }
        const ready = vehicles?.hovercraftReady(key) ?? false;
        const body = vehicles?.hovercraftBody(key) ?? new THREE.Group();
        const roster = NPC_ROSTER[e.rival];
        const plate = r?.plate ?? plateFor(roster?.name ?? e.name, roster?.livery ?? '#ffffff');
        body.add(plate);
        body.name = `race-rival:${e.name}`;
        this.scene.add(body);
        r = { entryId: id, rival: e.rival, body, plate, fallback: !ready };
        this.rivals.set(id, r);
      }
      // Heading from the track's tangent; the ride convention: forward = (−sin yaw, −cos yaw).
      const yaw = Math.atan2(-(q0.x - p0.x), -(q0.z - p0.z));
      const bob = Math.sin(now / 260 + e.rival) * 0.08;
      r.body.position.set(p.x, p.y + RIVAL_HOVER_M + bob, p.z);
      r.body.rotation.y = yaw;
    }
    for (const [id, r] of this.rivals) if (!keep.has(id)) { this.dropRival(r); this.rivals.delete(id); }
  }

  /** A rival leaves the drawing (out of range, the race over, the world gone): its livery plate — a CanvasTexture and a
   *  SpriteMaterial of its own — and a procedural skimmer's buffers are freed past the submit window (2026-09-25:
   *  only `clearRivals` freed the plate, so a rival riding in and out of the 1.8 km draw range leaked a plate each
   *  time, and a swapped skimmer's buffers were never freed). The plate is detached first: a Sprite's geometry is
   *  three's SHARED quad and must never go through deferDisposeTree. */
  private dropRival(r: Rival): void {
    r.body.remove(r.plate);
    deferDisposeTree(r.body); // the cooked hull clone's buffers are markShared (skipped); a skimmer's are its own
    const m = r.plate.material as THREE.SpriteMaterial;
    disposeLater(m.map);
    disposeLater(m);
  }

  /** The circuit polyline at arc length s (binary search on the 2 m samples). */
  private pointAt(c: Circuit, s: number): { x: number; y: number; z: number } | null {
    const pts = c.pts;
    const L = c.length;
    const ss = ((s % L) + L) % L;
    let lo = 0, hi = pts.length - 1;
    while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (pts[mid].s <= ss) lo = mid; else hi = mid; }
    const a = pts[lo], b = pts[hi];
    const span = Math.max(1e-6, b.s - a.s);
    const f = Math.max(0, Math.min(1, (ss - a.s) / span));
    return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, z: a.z + (b.z - a.z) * f };
  }

  private clearRivals(): void {
    for (const r of this.rivals.values()) this.dropRival(r); // deferred past the submit window (was freed the same frame it was drawn)
    this.rivals.clear();
    this.rivalsRace = null;
  }

  // ── Finishes: results + a light show over the line ─────────────────────────
  private noticeFinishes(): void {
    const me = atlas.conn?.identity;
    const g = this.gates();
    for (const ev of this.events()) {
      if (ev.state !== 'finished') continue;
      const key = ev.raceId.toString();
      if (this.finishSeen.has(key)) continue;
      this.finishSeen.add(key);
      if (Date.now() - ms(ev.finishedAt) > 60_000) continue; // an old result on subscribe: no ceremony
      const mine = me ? this.entries(ev.raceId).find((x) => !x.npc && x.identity.isEqual(me)) : undefined;
      if (mine) bus.emit('atlas:toast', { text: mine.place ? `Grand Circuit: you finished P${mine.place} — ${mine.points} points (${raceTime(mine.finishMs)})` : `Grand Circuit: the race is over — ${ev.winnerName} won`, kind: mine.place === 1 ? 'success' : 'info', ms: 9000 });
      if (g && atlas.currentWorldId === atlas.commonsWorldId) bus.emit('atlas:firework-show', { x: g.start[0], z: g.start[1], seed: Number(ev.raceId % 100000n) + 7, kind: 'race' });
    }
  }

  // ── The Town window's Races tab ────────────────────────────────────────────
  // SOFT RENDER (2026-09-25): built into a scratch and patched over the live host (lib/soft_render.ts) — a race row
  // callback no longer rebuilds the whole tab (and the window's scroll) under the player.
  private renderTab(real: HTMLElement): void { softRender(real, (h) => this.buildTab(h)); }
  private buildTab(host: HTMLElement): void {
    const now = Date.now();
    const live = this.myLive();
    const upcoming = this.events().filter((e) => e.state === 'queue' || e.state === 'grid').sort((a, b) => ms(a.startsAt) - ms(b.startsAt));
    const running = this.events().filter((e) => e.state === 'running');
    host.appendChild(head('The Grand Circuit'));
    host.appendChild(muted(`A race every half hour on the 31 km loop from the stadium campus. Queue for the next one; when the grid opens (3 minutes before the lights) be at the start line riding a hovercraft — a racer who is not on the grid when the lights go out misses the race. Twelve gates in order; named rivals fill the field; points by place (25, 18, 15, 12, 10, 8, 6, 4, 2, 1).`));
    const card0 = card();
    if (live) {
      const { e, ev } = live;
      card0.appendChild(el('div', 'font-weight:600;color:var(--ax-hi)', e.state === 'queued' ? `You race in ${untilText(ms(ev.startsAt) - now)}` : e.state === 'racing' ? 'You are racing' : `Finished — ${raceTime(e.finishMs)}`));
      const row = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
      const g = this.gates();
      if (g && e.state === 'queued') row.appendChild(btn('Waypoint to the line', () => void act(atlas.conn?.reducers.createWaypoint({ name: 'Grand Circuit — start', posX: g.start[0], posZ: g.start[1] }), 'Waypoint set on the start line')));
      if (e.state === 'queued' || e.state === 'racing') row.appendChild(btn(e.state === 'queued' ? 'Leave the card' : 'Retire', () => void this.leave()));
      card0.appendChild(row);
    } else {
      const next = upcoming[0];
      card0.appendChild(el('div', 'font-weight:600;color:var(--ax-hi)', next ? `Next race in ${untilText(ms(next.startsAt) - now)}` : 'The next race is carded when someone queues'));
      card0.appendChild(btn('Join the next race', () => void this.join(), true));
    }
    host.appendChild(card0);
    for (const ev of running) {
      const c = card();
      c.appendChild(el('div', 'font-weight:600', `Race #${ev.raceId} — running ${raceTime(now - ms(ev.startsAt))}`));
      const g = this.gates();
      const field = this.entries(ev.raceId).filter((x) => x.state === 'racing' || x.state === 'finished');
      const rows = field.map((x) => ({ x, p: g ? this.fieldProgress(ev, x, g) : 0 })).sort((a, b) => b.p - a.p);
      rows.forEach(({ x }, i) => c.appendChild(el('div', 'display:flex;gap:8px', `${i + 1}. ${x.name}${x.npc ? '' : ' ★'}${x.state === 'finished' ? ` — ${raceTime(x.finishMs)}` : ''}`)));
      host.appendChild(c);
    }
    const season = seasonOf(now / 1000);
    const board = this.standings(season).sort((a, b) => b.points - a.points || b.wins - a.wins || (a.bestMs || 1e12) - (b.bestMs || 1e12)).slice(0, 20);
    const ends = (seasonStartS(season + 1) * 1000) - now;
    host.appendChild(head(`This season's standings — the season ends in ${untilText(ends)}`));
    if (board.length === 0) host.appendChild(muted('No race has been run this season yet.'));
    const me = atlas.conn?.identity;
    board.forEach((s, i) => {
      const r = el('div', `display:grid;grid-template-columns:28px 1fr 60px 44px 44px 80px;gap:6px;align-items:center;padding:3px 6px;border-radius:var(--ax-radius);${me && !s.npc && s.identity.isEqual(me) ? 'background:var(--ax-sel)' : ''}`);
      r.appendChild(el('span', 'color:var(--ax-muted)', String(i + 1)));
      r.appendChild(el('span', s.npc ? '' : 'color:var(--ax-hi)', s.name));
      const pts = el('span', '', `${s.points} pts`); pts.className = 'ax-num';
      r.appendChild(pts);
      r.appendChild(el('span', 'color:var(--ax-soft)', `${s.wins}W`));
      r.appendChild(el('span', 'color:var(--ax-soft)', `${s.podiums}P`));
      const best = el('span', 'color:var(--ax-soft)', s.bestMs ? raceTime(s.bestMs) : '—'); best.className = 'ax-num';
      r.appendChild(best);
      host.appendChild(r);
    });
    const recent = this.events().filter((e) => e.state === 'finished').sort((a, b) => ms(b.finishedAt) - ms(a.finishedAt)).slice(0, 5);
    if (recent.length) {
      host.appendChild(head('Recent results'));
      for (const ev of recent) host.appendChild(muted(`Race #${ev.raceId} — won by ${ev.winnerName || '—'} (${ev.field} cars, ${ev.players} players)`));
    }
    void chip;
  }

  private fieldProgress(ev: EventRow, x: EntryRow, g: NonNullable<ReturnType<typeof circuitFor>>): number {
    const tMs = Date.now() - ms(ev.startsAt);
    if (x.state === 'finished') return 1e9 - x.finishMs;
    if (x.npc) { const a = npcArcAt(g, x.rival, `race:${ev.raceId}`, tMs, ev.laps || RACE_LAPS); return a.lap * g.length + a.s; }
    return x.gatesPassed * 1000;
  }

  private state(): Record<string, unknown> {
    const live = this.myLive();
    return {
      live: live ? { raceId: String(live.ev.raceId), state: live.ev.state, entry: live.e.state, gatesPassed: live.e.gatesPassed, startsInS: Math.round((ms(live.ev.startsAt) - Date.now()) / 1000) } : null,
      rivalsDrawn: this.rivals.size,
      rivalsNear: [...this.rivals.values()].map((r) => ({ rival: r.rival, fallback: r.fallback, x: Math.round(r.body.position.x), z: Math.round(r.body.position.z) })),
      hud: this.hud?.style.display !== 'none' ? this.hud?.textContent ?? '' : '',
      gateNote: this.gateNote,
      circuit: !!this.circuit(),
    };
  }
}
